import { useEffect, useState } from "react";
import { Routes, Route, NavLink, useNavigate, Navigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { chf, fdateShort } from "@/lib/format";
import { Loader, StatCard, EmptyState, PageHeader } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import ImageUpload from "@/components/ImageUpload";
import {
  LayoutDashboard, MessageSquare, Store, LogOut, Eye, Heart, Star, FileText, Plus, Send, Check, TrendingUp, Sparkles,
} from "lucide-react";

function Register({ onDone }) {
  const [f, setF] = useState({ business_name: "", category: "Photographes", city: "", canton: "Vaud", description: "", starting_price: 2000, cover_image: "", terms_accepted: false });
  const [cats, setCats] = useState([]);
  useEffect(() => { api.get("/marketplace/categories").then(({ data }) => setCats(data)); }, []);
  const submit = async () => {
    if (!f.terms_accepted) { toast.error("Veuillez accepter les conditions prestataires"); return; }
    try { await api.post("/vendors/register", { ...f, starting_price: +f.starting_price }); toast.success("Profil créé ! En attente de validation."); onDone(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  return (
    <div className="max-w-lg mx-auto card-lux p-8 mt-10">
      <div className="w-12 h-12 rounded-2xl bg-accent flex items-center justify-center mb-4"><Store className="w-5 h-5" /></div>
      <h1 className="font-serif text-2xl mb-1">Créer votre profil prestataire</h1>
      <p className="text-sm text-stone-500 mb-6">Rejoignez la marketplace des mariages romands.</p>
      <div className="space-y-3">
        <div><Label>Nom de l'entreprise</Label><Input data-testid="vr-name" value={f.business_name} onChange={(e) => setF({ ...f, business_name: e.target.value })} className="mt-1 h-10" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Catégorie</Label><select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className="mt-1 h-10 w-full rounded-md border px-2">{cats.map((c) => <option key={c}>{c}</option>)}</select></div>
          <div><Label>Canton</Label><select value={f.canton} onChange={(e) => setF({ ...f, canton: e.target.value })} className="mt-1 h-10 w-full rounded-md border px-2">{["Vaud", "Genève", "Valais", "Neuchâtel", "Fribourg", "Jura"].map((c) => <option key={c}>{c}</option>)}</select></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Ville</Label><Input value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} className="mt-1 h-10" /></div>
          <div><Label>Prix de départ (CHF)</Label><Input type="number" value={f.starting_price} onChange={(e) => setF({ ...f, starting_price: e.target.value })} className="mt-1 h-10" /></div>
        </div>
        <ImageUpload value={f.cover_image} onChange={(v) => setF({ ...f, cover_image: v })} label="Photo de couverture" testid="vr-cover" />
        <div><Label>Description</Label><Textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} rows={3} className="mt-1" /></div>
        <label className="flex items-start gap-2 text-sm text-stone-600" data-testid="vr-terms">
          <input type="checkbox" checked={f.terms_accepted} onChange={(e) => setF({ ...f, terms_accepted: e.target.checked })} className="mt-1" />
          <span>J'accepte les <a href="/terms" target="_blank" className="underline">conditions prestataires & marketplace</a>. Le contrat et le paiement des prestations se règlent directement avec le couple ; la plateforme ne prélève aucune commission.</span>
        </label>
        <Button data-testid="vr-submit" onClick={submit} disabled={!f.terms_accepted} className="w-full rounded-full">Créer mon profil</Button>
      </div>
    </div>
  );
}

function Promotion() {
  const [d, setD] = useState(null);
  const [amt, setAmt] = useState("");
  const load = async () => { const { data } = await api.get("/vendors/promotion"); setD(data); };
  useEffect(() => {
    load();
    const p = new URLSearchParams(window.location.search);
    const s = p.get("session_id"), c = p.get("contrib_session_id");
    if (s) api.get(`/vendors/promotion/status/${s}`).then(() => { toast.success("Mise en avant activée !"); load(); });
    if (c) api.get(`/vendors/promotion/status/${c}`).then(() => { toast.success("Merci pour votre contribution !"); load(); });
  }, []);
  const subscribe = async (plan_code) => {
    try { const { data } = await api.post("/vendors/promotion/checkout", { plan_code, origin_url: window.location.origin });
      window.location.href = data.checkout_url; }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const contribute = async (a) => {
    try { const { data } = await api.post("/vendors/contribution/checkout", { amount: +a, origin_url: window.location.origin });
      window.location.href = data.checkout_url; }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const cancel = async () => { const { data } = await api.post("/vendors/promotion/cancel"); toast.success(data.message); load(); };
  if (!d) return <Loader />;
  const active = d.featured;
  const isPartner = active && d.source === "admin_grant";
  return (
    <div>
      <PageHeader eyebrow="Visibilité" title="Mise en avant"
        description="Gagnez en visibilité dans la marketplace. Aucune commission n'est prélevée sur vos prestations." />
      {active && isPartner && (
        <div className="card-lux p-5 mb-5 ring-1 ring-sage/40 bg-sage/5" data-testid="promo-partner">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-sage/15 flex items-center justify-center flex-shrink-0"><Sparkles className="w-5 h-5 text-sage" /></div>
            <div>
              <div className="font-serif text-lg">Offre partenaire — {d.plan === "VENDOR_PRO" ? "Pro" : "Featured"}</div>
              <div className="text-sm text-stone-600">Offerte par la plateforme. Vous bénéficiez de la mise en avant complète, sans frais.
                {d.featured_until ? <> Valable jusqu'au {fdateShort(d.featured_until)}.</> : <> Sans date d'échéance.</>}</div>
              <div className="text-xs text-stone-400 mt-1">Vous n'avez rien à payer. Pour toute question, contactez l'équipe.</div>
            </div>
          </div>
        </div>
      )}
      {active && !isPartner && (
        <div className="card-lux p-5 mb-5" data-testid="promo-current">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div><div className="font-serif text-lg">Forfait actuel : {d.plan === "VENDOR_PRO" ? "Pro" : "Featured"}</div>
              <div className="text-sm text-stone-500">Statut : <b className="text-sage">{d.status === "cancelled" ? "Actif jusqu'à l'échéance (renouvellement annulé)" : "Actif"}</b>
                {d.featured_until && <> · Échéance : {fdateShort(d.featured_until)}</>}</div></div>
            {d.status !== "cancelled" && <Button variant="outline" onClick={cancel} data-testid="promo-cancel">Annuler le renouvellement</Button>}
          </div>
        </div>
      )}
      {!isPartner && (<>
      <div className="grid md:grid-cols-2 gap-4">
        {d.plans.sort((a, b) => (a.promotion_weight || 0) - (b.promotion_weight || 0)).map((p) => (
          <div key={p.code} className={`card-lux p-6 ${p.code === "VENDOR_PRO" ? "ring-1 ring-champagne" : ""}`} data-testid={`promo-plan-${p.code}`}>
            <div className="flex items-center gap-2 mb-1">{p.code === "VENDOR_PRO" ? <TrendingUp className="w-5 h-5 text-champagne" /> : <Sparkles className="w-5 h-5" />}
              <h3 className="font-serif text-xl">{p.name}</h3></div>
            <div className="font-mono text-3xl my-2">CHF {Number(p.amount).toFixed(2)}<span className="text-sm text-stone-400 font-sans"> / mois</span></div>
            <p className="text-sm text-stone-500 mb-4">{p.description}</p>
            <Button onClick={() => subscribe(p.code)} className="w-full rounded-full" data-testid={`subscribe-${p.code}`}>
              {active && d.plan === p.code ? "Renouveler / prolonger" : "S'abonner"}</Button>
          </div>
        ))}
      </div>
      <p className="text-xs text-stone-400 mt-3">Facturation mensuelle en CHF. La mise en avant améliore la visibilité mais ne garantit ni classement précis, ni demandes de devis, ni réservations. Chaque emplacement payant est signalé « Sponsorisé ».</p>
      </>)}

      <div className="card-lux p-6 mt-6" data-testid="contribution">
        <h3 className="font-serif text-lg mb-1">Soutenir la plateforme</h3>
        <p className="text-sm text-stone-500 mb-4">Vous aimez la plateforme et souhaitez soutenir son développement ? Vous pouvez contribuer librement. Cette contribution est entièrement facultative et n'influence ni votre classement, ni vos demandes de devis, ni l'accès aux fonctionnalités. Elle n'affecte pas le classement, les leads ou les fonctionnalités.</p>
        <div className="flex items-center gap-2 flex-wrap">
          {d.presets.map((a) => <Button key={a} variant="outline" className="rounded-full" onClick={() => contribute(a)} data-testid={`contrib-${a}`}>CHF {a}</Button>)}
          <Input type="number" value={amt} onChange={(e) => setAmt(e.target.value)} placeholder="Autre" className="h-10 w-28" data-testid="contrib-custom" />
          <Button className="rounded-full" onClick={() => amt && contribute(amt)} data-testid="contrib-submit">Contribuer</Button>
        </div>
        {d.contributions.length > 0 && <div className="text-xs text-stone-400 mt-3">Contributions : {d.contributions.map((c) => `CHF ${c.amount}`).join(", ")}</div>}
      </div>
    </div>
  );
}

function Dashboard() {
  const nav = useNavigate();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/vendors/dashboard").then(({ data }) => setD(data)).catch(() => setD(false)); }, []);
  if (d === null) return <Loader />;
  if (d === false) return null;
  const s = d.stats;
  return (
    <div>
      <PageHeader eyebrow="Espace prestataire" title={d.vendor.business_name}
        description={`Statut : ${d.vendor.status === "approved" ? "Approuvé ✓" : d.vendor.status === "pending" ? "En attente de validation" : d.vendor.status}`} />
      <div className="card-lux p-5 mb-6 max-w-md" data-testid="vendor-photo-card">
        <ImageUpload value={d.vendor.cover_image} label="Photo de couverture du profil"
          onChange={async (v) => {
            try {
              await api.patch(`/vendors/${d.vendor.id}`, { cover_image: v });
              setD({ ...d, vendor: { ...d.vendor, cover_image: v } });
              toast.success(v ? "Photo mise à jour" : "Photo supprimée");
            } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
          }} testid="vendor-cover" />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard label="Vues du profil" value={s.profile_views} icon={Eye} />
        <StatCard label="Favoris" value={s.favorites} icon={Heart} accent="blush" />
        <StatCard label="Demandes" value={s.inquiries} icon={MessageSquare} />
        <StatCard label="Note" value={`${s.rating}★`} sub={`${s.review_count} avis`} icon={Star} accent="champagne" />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Devis envoyés" value={s.quotes} accent="stone" />
        <StatCard label="Demandes de devis" value={s.inquiries} accent="sage" />
        <StatCard label="Clics contact" value={s.contact_clicks || 0} accent="champagne" />
        <StatCard label="Mise en avant" value={d.vendor.featured ? "Active" : "Standard"} accent={d.vendor.featured ? "sage" : "stone"} />
      </div>
      {!d.vendor.featured && (
        <div className="card-lux p-6 mt-4 flex items-center justify-between flex-wrap gap-3" data-testid="promo-cta">
          <div><div className="font-serif text-lg">Gagnez en visibilité</div>
            <div className="text-sm text-stone-500">Apparaissez en priorité dans votre catégorie et votre région avec un badge « Sponsorisé ».</div></div>
          <Button onClick={() => nav("/vendor/promotion")} className="rounded-full" data-testid="promo-cta-btn">Mettre mon profil en avant</Button>
        </div>
      )}
    </div>
  );
}

function VMessages() {
  const [convs, setConvs] = useState(null);
  const [active, setActive] = useState(null);
  const [thread, setThread] = useState(null);
  const [text, setText] = useState("");
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [quote, setQuote] = useState({ amount: 0, deposit: 0, notes: "" });

  const loadConvs = async () => { const { data } = await api.get("/vendors/conversations"); setConvs(data); if (!active && data[0]) setActive(data[0].id); };
  useEffect(() => { loadConvs(); }, []);
  const loadThread = async () => { if (!active) return; const { data } = await api.get(`/conversations/${active}/messages`); setThread(data); };
  useEffect(() => { loadThread(); }, [active]);

  const send = async () => { if (!text.trim()) return; await api.post(`/conversations/${active}/messages`, { text }); setText(""); loadThread(); };
  const sendQuote = async () => {
    await api.post(`/conversations/${active}/quotes`, {
      items: [{ description: "Prestation", quantity: 1, unit_price: +quote.amount }],
      deposit: +quote.deposit, notes: quote.notes,
    });
    toast.success("Devis envoyé"); setQuoteOpen(false); setQuote({ amount: 0, deposit: 0, notes: "" }); loadThread();
  };

  if (!convs) return <Loader />;
  if (convs.length === 0) return <EmptyState icon={MessageSquare} title="Aucune demande" description="Les demandes de devis des couples apparaîtront ici." />;
  return (
    <div>
      <PageHeader eyebrow="Espace prestataire" title="Demandes & devis" />
      <div className="card-lux grid lg:grid-cols-[280px_1fr] overflow-hidden h-[calc(100vh-260px)]">
        <div className="border-r overflow-y-auto">
          {convs.map((c) => (
            <button key={c.id} onClick={() => setActive(c.id)} className={`w-full text-left px-4 py-3 border-b ${active === c.id ? "bg-accent/40" : ""}`}>
              <div className="font-medium text-sm">{c.wedding_names}</div>
              {c.unread > 0 && <span className="text-xs text-champagne">Nouveau message</span>}</button>
          ))}
        </div>
        <div className="flex flex-col">
          {thread && (<>
            <div className="flex-1 overflow-y-auto p-5 space-y-3">
              {thread.messages.map((m) => {
                const mine = m.sender === "vendor";
                const q = m.type === "quote" ? thread.quotes.find((x) => x.id === m.quote_id) : null;
                return (
                  <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm ${mine ? "bg-stone-900 text-white" : "bg-accent"}`}>
                      {q ? <div>Devis {q.reference} — {chf(q.total)} {q.status === "accepted" && <span className="text-sage">(accepté)</span>}</div> : m.text}</div>
                  </div>
                );
              })}
            </div>
            <div className="border-t p-3 flex gap-2">
              <Button variant="outline" onClick={() => setQuoteOpen(true)} data-testid="vendor-quote-btn"><FileText className="w-4 h-4" /></Button>
              <Input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Répondre…" className="h-11" />
              <Button onClick={send} className="rounded-full h-11 px-4"><Send className="w-4 h-4" /></Button>
            </div>
          </>)}
        </div>
      </div>
      {quoteOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setQuoteOpen(false)}>
          <div className="card-lux p-6 w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-serif text-lg mb-4">Créer un devis</h3>
            <div className="space-y-3">
              <div><Label>Montant total (CHF)</Label><Input type="number" value={quote.amount} onChange={(e) => setQuote({ ...quote, amount: e.target.value })} className="mt-1 h-10" data-testid="quote-amount" /></div>
              <div><Label>Acompte (CHF)</Label><Input type="number" value={quote.deposit} onChange={(e) => setQuote({ ...quote, deposit: e.target.value })} className="mt-1 h-10" /></div>
              <div><Label>Notes</Label><Textarea value={quote.notes} onChange={(e) => setQuote({ ...quote, notes: e.target.value })} rows={2} className="mt-1" /></div>
              <Button onClick={sendQuote} className="w-full rounded-full" data-testid="send-quote">Envoyer le devis</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function VendorPortal() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [vendor, setVendor] = useState(undefined);
  useEffect(() => { api.get("/vendors/mine").then(({ data }) => setVendor(data.vendor)); }, []);

  if (vendor === undefined) return <Loader />;
  if (!vendor) return (
    <div className="min-h-screen bg-background">
      <div className="h-16 border-b flex items-center px-6 justify-between">
        <span className="font-serif text-lg">Espace Prestataire</span>
        <Button variant="ghost" onClick={() => nav("/app")}>Retour couple</Button></div>
      <Register onDone={() => api.get("/vendors/mine").then(({ data }) => setVendor(data.vendor))} />
    </div>
  );

  return (
    <div className="min-h-screen flex bg-background">
      <aside className="w-64 border-r bg-white/70 hidden lg:flex flex-col">
        <div className="h-16 flex items-center px-5 border-b font-serif text-lg">Prestataire</div>
        <nav className="flex-1 p-3 space-y-1">
          {[["", "Tableau de bord", LayoutDashboard], ["messages", "Demandes & devis", MessageSquare], ["promotion", "Mise en avant", Sparkles]].map(([to, l, Icon]) => (
            <NavLink key={to} to={`/vendor/${to}`} end={to === ""} className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm ${isActive ? "bg-stone-900 text-white" : "text-stone-600 hover:bg-stone-100"}`}>
              <Icon className="w-4 h-4" /> {l}</NavLink>
          ))}
        </nav>
        <div className="p-3 border-t space-y-1">
          <button onClick={() => nav("/app")} className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-stone-600 hover:bg-stone-100 w-full"><Store className="w-4 h-4" /> Espace couple</button>
          <button onClick={logout} className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-stone-600 hover:bg-stone-100 w-full"><LogOut className="w-4 h-4" /> Déconnexion</button>
        </div>
      </aside>
      <main className="flex-1 px-4 lg:px-8 py-8 max-w-6xl mx-auto w-full">
        <Routes>
          <Route index element={<Dashboard />} />
          <Route path="messages" element={<VMessages />} />
          <Route path="promotion" element={<Promotion />} />
          <Route path="*" element={<Navigate to="/vendor" replace />} />
        </Routes>
      </main>
    </div>
  );
}
